import React, { useState, useRef, useEffect } from 'react';
import { lastDayOfMonth, subMonths, format } from 'date-fns';
import styled from '@emotion/styled';
import Dialog, { Title, Content, DialogActions } from 'ui/views/dialogs/Dialog';
import { KpiWithDisplay } from 'domain/kpis/types';
import KpiAPI, { AddOrDeleteKpiValue } from 'apis/KpiAPI';
import useLazyResource from 'util/resource/useLazyResource';
import useNotify from 'hooks/useNotify';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';
import { contentSpacing, halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';
import Button from 'ui/elements/buttons/Button';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { asMonthNameYear } from 'util/dateUtils';
import { Link } from 'react-router-dom';
import EditIcon from 'ui/elements/icons/EditIcon';
import CustomIconButton from 'ui/elements/icons/IconButton';
import { KpiNumericInput } from '../KpiNumericInput';
import { companyUrls } from 'urls';
import { invalidate } from 'hooks/useSWR';

const TableContainer = styled.div`
  overflow-x: auto;
  margin: ${contentSpacing} 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const Th = styled.th`
  text-align: left;
  padding: ${halfSpacing} ${contentSpacing};
  font-size: ${bluePlanetTheme.typography.body1.fontSize};
  font-weight: ${bluePlanetTheme.typography.fontWeightBold};
  color: ${bluePlanetTheme.bluePlanetPalette.indigo.main};
  border-bottom: 2px solid ${bluePlanetTheme.palette.divider};
  white-space: nowrap;

  &:first-of-type {
    padding-left: 0;
  }
`;

const Td = styled.td`
  padding: ${halfSpacing} ${contentSpacing};

  &:first-of-type {
    padding-left: 0;
  }
`;

const KpiNameCell = styled(Td)`
  font-weight: ${bluePlanetTheme.typography.fontWeightMedium};
  color: ${bluePlanetTheme.bluePlanetPalette.indigo.main};
  min-width: 12rem;
`;

const ActionCell = styled(Td)`
  text-align: center;
  width: 3rem;
`;

const UnitText = styled.span`
  margin-left: ${halfSpacing};
  color: ${bluePlanetTheme.bluePlanetPalette.grey.dark};
  font-size: ${bluePlanetTheme.typography.body1.fontSize};
`;

const MobileKpiCard = styled.div`
  margin-bottom: ${contentSpacing};
`;

const MobileKpiName = styled.div`
  font-weight: ${bluePlanetTheme.typography.fontWeightMedium};
  font-size: ${bluePlanetTheme.typography.body1.fontSize};
  color: ${bluePlanetTheme.bluePlanetPalette.indigo.main};
  padding-top: ${halfSpacing};
  border-top: 2px solid ${bluePlanetTheme.palette.divider};

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const MobileMonthRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${quarterSpacing};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const MobileMonthLabel = styled.span`
  font-size: ${bluePlanetTheme.typography.body1.fontSize};
  color: ${bluePlanetTheme.bluePlanetPalette.grey.dark};
`;

const MobileInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${halfSpacing};
`;

interface KpiBulkUpdateDialogProps {
  kpis: KpiWithDisplay[];
  companyId: number;
  companySlug: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface KpiValueState {
  [kpiId: string]: {
    [monthKey: string]: string;
  };
}

// Helper to initialize values from KPI data
const getInitialValues = (kpis: KpiWithDisplay[]): KpiValueState => {
  const initialValues: KpiValueState = {};

  kpis.forEach(kpi => {
    initialValues[kpi.details.id] = {};
    kpi.values.forEach(v => {
      const monthKey = lastDayOfMonth(new Date(v.timestamp)).toISOString();
      initialValues[kpi.details.id][monthKey] = v.value.toString();
    });
  });

  return initialValues;
};

export default function KpiBulkUpdateDialog({
  kpis,
  companyId,
  companySlug,
  isOpen,
  onClose,
  onSuccess,
}: KpiBulkUpdateDialogProps) {
  const notify = useNotify();
  const { setLastUpdated } = useCompanyLastUpdated(companyId);
  const { isMobile } = useBreakpoints();
  const prevIsOpenRef = useRef(false); // Always start as false to detect first open
  const hasInitializedRef = useRef(false); // Track if we've initialized values for current dialog session

  // Start with last 3 months (previous month and 2 months before that)
  const [monthsToShow, setMonthsToShow] = useState(3);
  const [values, setValues] = useState<KpiValueState>({});
  const originalValuesRef = useRef<KpiValueState>({});
  const inputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());

  // Generate list of months - start from last month
  const months = Array.from({ length: monthsToShow }, (_, i) => {
    return lastDayOfMonth(subMonths(new Date(), i + 1));
  });

  // Initialize or reset values when dialog opens or when kpis data loads
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    const hasData = kpis.length > 0;

    // Reset initialization flag when dialog closes
    if (!isOpen && wasOpen) {
      hasInitializedRef.current = false;
    }

    // Initialize values when:
    // 1. Dialog opens (transition from closed to open) with data
    // 2. Dialog is already open, data arrives, and we haven't initialized yet (for page refresh case)
    if (isOpen && hasData && !hasInitializedRef.current) {
      const initialValues = getInitialValues(kpis);
      setValues(initialValues);
      originalValuesRef.current = JSON.parse(JSON.stringify(initialValues)); // Deep clone
      setMonthsToShow(3);
      hasInitializedRef.current = true;
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, kpis]);

  const [saveValues] = useLazyResource(
    (updates: AddOrDeleteKpiValue[]) => KpiAPI.kpi.updateValues(companyId, updates),
    {
      onSuccess: () => {
        setLastUpdated(new Date());
        onSuccess();
        notify('success', 'Saved');
        // Invalidate KPI list cache so embedded KPIs show fresh data
        invalidate(KpiAPI.kpi.list(companyId));
      },
      onFailure: () => {
        notify('error', 'Failed to save KPI value');
      },
    },
  );

  const saveValueInstantly = (kpiId: string, monthKey: string, value: string) => {
    const updates: Array<AddOrDeleteKpiValue> = [];
    const numericValue = parseFloat(value);

    if (value !== '' && !isNaN(numericValue)) {
      // Add value
      updates.push({
        add: {
          kpiId,
          value: numericValue,
          date: monthKey,
        },
      });
    } else if (value === '') {
      // Delete when cleared
      updates.push({
        delete: {
          kpiId,
          date: monthKey,
        },
      });
    }

    if (updates.length > 0) {
      saveValues(updates);
    }
  };

  const handleValueChange = (kpiId: string, monthKey: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [kpiId]: {
        ...prev[kpiId],
        [monthKey]: value,
      },
    }));
  };

  const handleBlur = (kpiId: string, monthKey: string) => {
    const currentValue = values[kpiId]?.[monthKey] || '';
    const originalValue = originalValuesRef.current[kpiId]?.[monthKey] || '';

    // Only save if the value has changed
    if (currentValue !== originalValue) {
      saveValueInstantly(kpiId, monthKey, currentValue);
      // Update the original value after successful save
      if (!originalValuesRef.current[kpiId]) {
        originalValuesRef.current[kpiId] = {};
      }
      originalValuesRef.current[kpiId][monthKey] = currentValue;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, kpiIndex: number, monthIndex: number) => {
    const totalKpis = activeKpis.length;
    const totalMonths = months.length;

    // Navigate with arrow keys
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      // Move to next month (right)
      const nextMonthIndex = monthIndex + 1;
      if (nextMonthIndex < totalMonths) {
        const inputKey = `${activeKpis[kpiIndex].details.id}-${months[nextMonthIndex].toISOString()}`;
        inputRefs.current.get(inputKey)?.focus();
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      // Move to previous month (left)
      const prevMonthIndex = monthIndex - 1;
      if (prevMonthIndex >= 0) {
        const inputKey = `${activeKpis[kpiIndex].details.id}-${months[prevMonthIndex].toISOString()}`;
        inputRefs.current.get(inputKey)?.focus();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Move to next KPI (down)
      const nextKpiIndex = kpiIndex + 1;
      if (nextKpiIndex < totalKpis) {
        const inputKey = `${activeKpis[nextKpiIndex].details.id}-${months[monthIndex].toISOString()}`;
        inputRefs.current.get(inputKey)?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Move to previous KPI (up)
      const prevKpiIndex = kpiIndex - 1;
      if (prevKpiIndex >= 0) {
        const inputKey = `${activeKpis[prevKpiIndex].details.id}-${months[monthIndex].toISOString()}`;
        inputRefs.current.get(inputKey)?.focus();
      }
    }
  };

  const activeKpis = kpis.filter(kpi => !kpi.details.archivedAt);

  if (activeKpis.length === 0) {
    return (
      <Dialog open={isOpen} onClose={onClose} maxWidth="md">
        <Title onClose={onClose}>Update KPIs</Title>
        <Content>
          <p className="text-body">You don't have any active KPIs. Please add KPIs first.</p>
        </Content>
        <DialogActions>
          <Button kind="tertiary" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" mobileLayout="fullscreen">
      <Title onClose={onClose}>Update KPIs</Title>
      <Content>
        <p className="text-body u-content-spacing-bottom">
          Enter values for your KPIs. {!isMobile && 'The most recent month is shown first.'}
        </p>

        {!isMobile ? (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th></Th>
                  {months.map(month => (
                    <Th key={month.toISOString()}>{format(month, 'MMM yyyy')}</Th>
                  ))}
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {activeKpis.map((kpi, kpiIndex) => (
                  <tr key={kpi.details.id}>
                    <KpiNameCell>{kpi.details.name}</KpiNameCell>
                    {months.map((month, monthIndex) => {
                      const monthKey = month.toISOString();
                      const value = values[kpi.details.id]?.[monthKey];
                      const inputKey = `${kpi.details.id}-${monthKey}`;

                      return (
                        <Td key={monthKey}>
                          <KpiNumericInput
                            width="8rem"
                            value={value !== undefined ? value : ''}
                            getInputRef={(ref: HTMLInputElement | null) => {
                              if (ref) {
                                inputRefs.current.set(inputKey, ref);
                              } else {
                                inputRefs.current.delete(inputKey);
                              }
                            }}
                            onKeyDown={(e: any) => handleKeyDown(e, kpiIndex, monthIndex)}
                            onBlur={() => handleBlur(kpi.details.id, monthKey)}
                            onChange={(e: any) => {
                              const newValue = e.target.value.replace(/\s/g, '');
                              handleValueChange(kpi.details.id, monthKey, newValue);
                            }}
                          />
                          {kpi.details.unitOfMeasure && <UnitText>{kpi.details.unitOfMeasure}</UnitText>}
                        </Td>
                      );
                    })}
                    <ActionCell>
                      <Link to={companyUrls.kpi.edit(companySlug, kpi.details.id)} title="Edit KPI settings">
                        <CustomIconButton color="indigo" hoverColor="blue">
                          <EditIcon fontSize="small" />
                        </CustomIconButton>
                      </Link>
                    </ActionCell>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        ) : (
          <div>
            {activeKpis.map((kpi, kpiIndex) => (
              <MobileKpiCard key={kpi.details.id}>
                <MobileKpiName
                  style={{ borderTop: kpiIndex === 0 ? 'none' : undefined, paddingTop: kpiIndex === 0 ? 0 : undefined }}
                >
                  {kpi.details.name}
                </MobileKpiName>
                {months.map((month, monthIndex) => {
                  const monthKey = month.toISOString();
                  const value = values[kpi.details.id]?.[monthKey];
                  const inputKey = `${kpi.details.id}-${monthKey}`;

                  return (
                    <MobileMonthRow key={monthKey}>
                      <MobileMonthLabel>{asMonthNameYear(month)}</MobileMonthLabel>
                      <MobileInputContainer>
                        <KpiNumericInput
                          width="8rem"
                          value={value !== undefined ? value : ''}
                          getInputRef={(ref: HTMLInputElement | null) => {
                            if (ref) {
                              inputRefs.current.set(inputKey, ref);
                            } else {
                              inputRefs.current.delete(inputKey);
                            }
                          }}
                          onKeyDown={(e: any) => handleKeyDown(e, kpiIndex, monthIndex)}
                          onBlur={() => handleBlur(kpi.details.id, monthKey)}
                          onChange={(e: any) => {
                            const newValue = e.target.value.replace(/\s/g, '');
                            handleValueChange(kpi.details.id, monthKey, newValue);
                          }}
                        />
                        {kpi.details.unitOfMeasure && <UnitText>{kpi.details.unitOfMeasure}</UnitText>}
                      </MobileInputContainer>
                    </MobileMonthRow>
                  );
                })}
                <MobileMonthRow>
                  <Link className="text-link-small" to={companyUrls.kpi.edit(companySlug, kpi.details.id)}>
                    Edit KPI
                  </Link>
                </MobileMonthRow>
              </MobileKpiCard>
            ))}
          </div>
        )}
      </Content>
      <DialogActions>
        <Button kind="tertiary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
